<template>
  <v-form ref="form" class="register" v-model="valid" lazy-validation autocomplete="off">
    <h3 class="mb-3 grey--text">Sign Up Form: Maryland EMS Online Training Center</h3>
    <v-card class="mx-auto px-3 pb-8" elevation="6">
      <v-card-text>
        <v-row>
          <v-col cols="12" sm="6">
            <v-text-field
              outlined
              v-model.trim="firstName"
              :error-messages="firstNameErrors"
              label="First Name"
              required
              @input="$v.firstName.$touch()"
              @blur="$v.firstName.$touch()"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              outlined
              v-model.trim="lastName"
              :error-messages="lastNameErrors"
              label="Last Name"
              required
              @input="$v.lastName.$touch()"
              @blur="$v.lastName.$touch()"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="6">
            <v-text-field
              outlined
              v-model.trim="email"
              :error-messages="emailErrors"
              label="Email Address"
              required
              @input="$v.email.$touch()"
              @blur="$v.email.$touch()"
            >
              <v-btn slot="append" v-show="loading" loading text x-small color="primary"></v-btn>
            </v-text-field>
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              outlined
              v-model.trim="email2"
              :error-messages="email2Errors"
              label="Confirm Email Address"
              required
              @input="$v.email2.$touch()"
              @blur="$v.email2.$touch()"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="6">
            <v-combobox
              outlined
              v-model="certLevel"
              :items="certifications"
              item-text="text"
              :error-messages="certLevelErrors"
              label="Cert Level"
              required
              @blur="$v.certLevel.$touch()"
              @change="onChanged"
            ></v-combobox>
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              outlined
              :prefix="certLevelPrefix"
              v-model.trim="licenseNumber"
              :error-messages="licenseNumberErrors"
              maxlength="7"
              hint="Only numbers are allowed in this field"
              label="License Number"
              required
              @input="$v.licenseNumber.$touch()"
              @blur="$v.licenseNumber.$touch()"
              @keydown="onKeydown"
            >
              <v-btn slot="append" v-show="licenseLoading" loading text x-small color="primary"></v-btn>
            </v-text-field>
          </v-col>
        </v-row>
        <v-checkbox
          v-model="consent"
          :error-messages="consentErrors"
          label="By clicking submit, I verify that the personal information that I have entered is accurate.  I further confirm that I will use the MIEMSS Online Training Center account solely for the educational purposes for which it was designed.   I understand and acknowledge that any misuse or abuse of this account, as determined by MIEMSS, will result in the termination of my MIEMSS Online Training Center account."
          required
          class="consent"
          @change="$v.consent.$touch()"
          @blur="$v.consent.$touch()"
        ></v-checkbox>
      </v-card-text>
      <v-card-actions>
        <v-btn
          :disabled="loading || !valid"
          color="success"
          class="mr-4"
          @click="submit"
        >Submit Form</v-btn>
        <v-btn color="error" @click="reset">Reset Form</v-btn>
      </v-card-actions>
    </v-card>
    <Confirmation
      title="Registration Complete"
      :dialog="showConfirmation"
      @Close="showConfirmation = $event"
    />
  </v-form>
</template>

<script>
import { validationMixin } from "vuelidate";
import { required, email, sameAs } from "vuelidate/lib/validators";
import debounce from "lodash/debounce";
import dateformat from "dateformat";
import Confirmation from "../components/Confirmation";
import db from "@/fb";

function checkEmail(e) {
  if (e && email(e)) {
    this.loading = true;
    this.debounceEmail(e);
  }
  return !this.userExists;
}

function checkLicense(e) {
  if (this.certLevelPrefix && e.length > 4) {
    this.licenseLoading = true;
    this.debounceLicense(e);
  }
  return !this.licenseExists;
}

export default {
  mixins: [validationMixin],

  validations: {
    firstName: { required },
    lastName: { required },
    email: { required, email, checkEmail },
    email2: {
      required,
      email,
      confirm: sameAs("email"),
    },
    certLevel: { required },
    licenseNumber: {
      required,
      check: (value) => {
        return /^[0-9]*$/.test(value);
      },
      checkLicense,
    },

    consent: {
      checked(val) {
        return val;
      },
    },
  },

  components: {
    Confirmation,
  },

  data: () => ({
    valid: true,
    userExists: false,
    loading: false,
    licenseExists: false,
    licenseLoading: false,
    certLevelPrefix: "",
    showConfirmation: false,

    firstName: "",
    lastName: "",
    email: "",
    email2: "",
    certLevel: "",
    licenseNumber: "",
    certifications: [
      { text: "Registered Nurse (NR)", value: "RN", prefix: "R" },
      { text: "Nurse Practitioner (NP)", value: "NP", prefix: "R" },
      { text: "Physician (DO)", value: "DO", prefix: "H" },
      { text: "Physician (MD)", value: "MD", prefix: "D" },
      { text: "Physician Assistant (PA)", value: "PA", prefix: "C" },
    ],
    consent: false,
  }),

  computed: {
    consentErrors() {
      const errors = [];
      if (!this.$v.consent.$dirty) return errors;
      !this.$v.consent.checked && errors.push("You must agree to continue!");
      return errors;
    },
    certLevelErrors() {
      const errors = [];
      if (!this.$v.certLevel.$dirty) return errors;
      !this.$v.certLevel.required &&
        errors.push("Certification level is required");
      return errors;
    },
    firstNameErrors() {
      const errors = [];
      if (!this.$v.firstName.$dirty) return errors;
      !this.$v.firstName.required && errors.push("First Name is required.");
      return errors;
    },
    lastNameErrors() {
      const errors = [];
      if (!this.$v.lastName.$dirty) return errors;
      !this.$v.lastName.required && errors.push("Last Name is required.");
      return errors;
    },
    emailErrors() {
      const errors = [];
      if (!this.$v.email.$dirty) return errors;
      !this.$v.email.email && errors.push("Must be valid Email Address");
      !this.$v.email.required && errors.push("Email Address is required");
      !this.$v.email.checkEmail && errors.push("Email Address already in use");
      return errors;
    },
    email2Errors() {
      const errors = [];
      if (!this.$v.email2.$dirty) return errors;
      !this.$v.email2.required && errors.push("Email Address is required");
      !this.$v.email2.email && errors.push("Must be valid Email Address");
      !this.$v.email2.confirm && errors.push("Email Address not identical");
      return errors;
    },
    licenseNumberErrors() {
      const errors = [];
      if (!this.$v.licenseNumber.$dirty) return errors;
      !this.$v.licenseNumber.required &&
        errors.push("License Number is required.");
      !this.$v.licenseNumber.check && errors.push("Must be numeric");
      !this.$v.licenseNumber.checkLicense &&
        errors.push(`License # ${this.licenseNumber} already in user`);
      return errors;
    },
  },

  methods: {
    debounceEmail: debounce(
      async function (e) {
        const baseURI = this.$apiBaseURI + "RegisterUser/email-exists";
        const result = await this.$http.post(baseURI, { email: e });
        this.loading = false;
        this.userExists = result.data;
      },
      1000,
      { leading: false, trailing: true }
    ),
    debounceLicense: debounce(
      async function (e) {
        const url =
          this.$apiBaseURI + `registerUser/${this.certLevelPrefix + e}`;
        const result = await this.$http.get(url);
        this.licenseLoading = false;
        this.licenseExists = result.data;
      },
      2000,
      { leading: false, trailing: true }
    ),

    onKeydown(e) {
      //const allowedChars = "0123456789";
      function contains(stringValue, charValue) {
        return stringValue.indexOf(charValue) > -1;
      }
      const invalidKey = e.key.length === 1 && !contains("0123456789", e.key);
      invalidKey && e.preventDefault();
    },

    onChanged(e) {
      this.$v.certLevel.$touch();
      this.licenseNumber = "";
      this.certLevelPrefix = e !== null ? e.prefix : "";
    },

    async submit() {
      this.$v.$touch();
      if (!this.$v.$invalid && !this.loading) {
        try {
          const baseURI = this.$apiBaseURI + "registeruser";

          const { firstName, lastName, email, licenseNumber } = this.$data;
          const certLevel = this.$data.certLevel.text;
          const licenseNum = this.$data.certLevel.prefix + licenseNumber;
          const payload = {
            firstName,
            lastName,
            email,
            certLevel,
            licenseNumber: licenseNum,
            createdOn: dateformat(new Date(), "isoDateTime"),
          };

          await this.$http.post(baseURI, payload);

          db.collection("moodle-users")
            .add(payload)
            .then(() => {
              this.showConfirmation = true;
              this.reset();
            })
            .catch((err) => console.error(err));
        } catch (error) {
          console.log("ERROR", error);
        }
      }
    },
    reset() {
      this.$v.$reset();
      this.firstName = "";
      this.lastName = "";
      this.email = "";
      this.email2 = "";
      this.certLevel = "";
      this.licenseNumber = "";
      this.consent = false;
      this.loading = false;
      this.userExists = false;
      this.certLevelPrefix = "";
      this.licenseLoading = false;
      this.licenseExists = false;
    },
  },
};
</script>

<style>
.register .consent .v-input__slot {
  align-items: flex-start !important;
}
</style>
